import type { MatchAndClubCountryCode, PlayerFFTranking, UserGender } from "@teamz/common";

import CupIcon from "@/assets/cup.svg";
import RankingIcon from "@/assets/ranking.svg";
import TournamentIcon from "@/assets/tournaments.svg";
import { matchAndClubCountryCodes, playerFFTranking } from "@teamz/common";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ConversionTableModal } from "@/components/conversion-table-modal";
import { Select, SelectContent, SelectGroup, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RootField, TextField } from "@/components/ui/text-field";
import { useMe } from "@/context/user-context";
import { useTranslationUtils } from "@/hooks/use-translation-utils";
import { cn } from "@/lib/utils";

type TabType = "matchs" | "tournaments";

export function FilterBarPlayerList(
  {
    search,
    country,
    gender,
    FFTRanking,
    activeTab,
    setSearch,
    setCountryFilter,
    setGenderFilter,
    setFFTFilter,
    setActiveTab,
    listType,
  }:
  {
    search?: string;
    country?: MatchAndClubCountryCode[number];
    gender?: UserGender[number];
    FFTRanking?: PlayerFFTranking[number];
    activeTab?: TabType[number];
    setSearch: (search: string) => void;
    setCountryFilter?: (country: MatchAndClubCountryCode) => void;
    setGenderFilter?: (gender: UserGender) => void;
    setFFTFilter?: (ranking: PlayerFFTranking) => void;
    setActiveTab: (tab: TabType) => void;
    listType: "favorites" | "players";
  },
) {
  const { getCountryName } = useTranslationUtils();
  const { t } = useTranslation(["common"]);
  const [modalState, updateModalState] = useState<"open" | "close">("close");
  const { user } = useMe();

  const shouldDisabledMatchTab = !user.isAdmin && user.plan === "pack_tournoi";
  const shouldDisabledTournamentTab = !user.isAdmin && user.plan === "pack_equipe";

  return (
    <div>
      <div className={cn(
        "grid grid-cols-1 items-center gap-y-2 bg-neutral-700 p-3 sm:grid-cols-2 md:px-4 md:py-5 lg:grid-cols-4 lg:gap-x-4",
        listType === "favorites" ? "md:rounded-xl" : "md:rounded-t-xl",
      )}
      >
        <h1 className="order-1 col-span-1 shrink-0 text-nowrap text-center text-lg font-medium text-white sm:text-start md:text-start md:text-xl">
          {listType === "favorites"
            ? t("common:favorites-list")
            : t("common:players-list")}
        </h1>

        <RootField className="order-2 col-span-2 flex-1 sm:order-3 lg:order-2">
          <TextField
            clearable
            onClear={() => {
              setSearch("");
            }}
            value={search}
            placeholder={t("common:search-player")}
            className="col-span-2 flex-1 border-0 text-white"
            onChange={event => setSearch(event.target.value)}
          />
        </RootField>

        <div className="order-3 col-span-1 flex h-12 w-full rounded-lg bg-neutral-500 p-0.5 sm:order-2 sm:w-fit sm:justify-self-end lg:order-3">
          <div
            className={cn("flex size-full cursor-pointer items-center justify-center gap-1.5 rounded-md border-0 px-2.5 text-white data-[state=active]:border-0 data-[state=active]:bg-neutral-700 data-[state=active]:text-white", shouldDisabledMatchTab && "pointer-events-none opacity-50",
            )}
            onClick={() => {
              if (shouldDisabledMatchTab)
                return;
              setActiveTab("matchs");
            }}
            data-state={activeTab === "matchs" ? "active" : "inactive"}
          >
            <CupIcon className="size-5 shrink-0 text-orange-500" />
            <span className="text-sm font-medium">Matchs</span>
          </div>
          <div
            className={cn("flex size-full cursor-pointer items-center justify-center gap-1.5 rounded-md border-0 px-2 text-white data-[state=active]:border-0 data-[state=active]:bg-neutral-700 data-[state=active]:text-white", shouldDisabledTournamentTab && "pointer-events-none opacity-50",
            )}
            onClick={() => {
              if (shouldDisabledTournamentTab)
                return;
              setActiveTab("tournaments");
            }}
            data-state={activeTab === "tournaments" ? "active" : "inactive"}
          >
            <TournamentIcon className="size-5 shrink-0 text-orange-500" />
            <span className="text-sm font-medium">{t("common:desktop-nav.tournaments")}</span>
          </div>
        </div>
      </div>

      {listType === "players" && setCountryFilter && setGenderFilter && setFFTFilter && (
        <>
          <div className="flex flex-col gap-2 bg-neutral-600 p-3 md:flex-row md:items-center md:justify-between md:gap-4 md:rounded-b-xl">
            <div className="flex w-full items-center gap-2">
              <Select onValueChange={value => setCountryFilter(value as MatchAndClubCountryCode)} value={country}>
                <SelectTrigger className="border-0 bg-neutral-700 text-white">
                  <SelectValue placeholder={t("common:availability-countries")} className="text-white" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {/* @ts-expect-error Error type on null value */}
                    <SelectItem value={null} className="font-semibold">
                      {t("common:see-all")}
                    </SelectItem>
                    <SelectSeparator />
                    {matchAndClubCountryCodes.map(country => (
                      <SelectItem key={country} value={country}>
                        {getCountryName(country.toUpperCase() as MatchAndClubCountryCode)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>

              <Select onValueChange={value => setGenderFilter(value as UserGender)} value={gender}>
                <SelectTrigger className="border-0 bg-neutral-700 text-white ">
                  <SelectValue placeholder={t("common:gender")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {/* @ts-expect-error Error type on null value */}
                    <SelectItem value={null} className="font-semibold">
                      {t("common:see-all")}
                    </SelectItem>
                    <SelectSeparator />
                    <SelectItem value="male">{t("common:man")}</SelectItem>
                    <SelectItem value="female">{t("common:woman")}</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>

              <Select onValueChange={value => setFFTFilter(value as PlayerFFTranking)} value={FFTRanking}>
                <SelectTrigger className="hidden border-0 bg-neutral-700 text-white sm:flex">
                  <SelectValue placeholder={t("common:FFT-ranking")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {/* @ts-expect-error Error type on null value */}
                    <SelectItem value={null} className="font-semibold">
                      {t("common:see-all")}
                    </SelectItem>
                    <SelectSeparator />
                    {
                      playerFFTranking.map(ranking => (
                        <SelectItem key={ranking} value={ranking}>
                          {ranking}
                        </SelectItem>
                      ))
                    }
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <Select onValueChange={value => setFFTFilter(value as PlayerFFTranking)} value={FFTRanking}>
              <SelectTrigger className="border-0 bg-neutral-700 text-white sm:hidden">
                <SelectValue placeholder={t("common:FFT-ranking")} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {/* @ts-expect-error Error type on null value */}
                  <SelectItem value={null} className="font-semibold">
                    {t("common:see-all")}
                  </SelectItem>
                  <SelectSeparator />
                  {
                    playerFFTranking.map(ranking => (
                      <SelectItem key={ranking} value={ranking}>
                        {ranking}
                      </SelectItem>
                    ))
                  }
                </SelectGroup>
              </SelectContent>
            </Select>

            <button type="button" onClick={() => updateModalState("open")} className="flex items-center justify-center gap-1.5 text-nowrap text-sm font-medium text-neutral-200">
              {t("common:rankings-equivalence")}
              <RankingIcon className="size-5 text-orange-500" />
            </button>

          </div>

          <ConversionTableModal modalOpen={modalState === "open"} closeModal={() => updateModalState("close")} />
        </>
      )}

    </div>
  );
}
